import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Mail, CheckCircle, XCircle, Clock, RefreshCw, Trash2, Send, FileText, UserPlus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailMonitorPage() {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query params
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('email_type', typeFilter);
      params.append('limit', '100');
      
      const [statsRes, logsRes] = await Promise.all([
        axios.get(`${API}/admin/email-stats`),
        axios.get(`${API}/admin/email-logs?${params.toString()}`)
      ]);
      
      setStats(statsRes.data);
      setLogs(logsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dati');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeleteLog = async (logId) => {
    if (!window.confirm('Eliminare questo log?')) return;
    try {
      await axios.delete(`${API}/admin/email-logs/${logId}`);
      toast.success('Log eliminato');
      fetchData();
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  const handleClearOldLogs = async () => {
    if (!window.confirm('Eliminare tutti i log più vecchi di 30 giorni?')) return;
    try {
      const response = await axios.delete(`${API}/admin/email-logs?days_to_keep=30`);
      toast.success(response.data.message);
      fetchData();
    } catch {
      toast.error('Errore pulizia log');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEmailTypeIcon = (type) => {
    switch (type) {
      case 'report_final':
        return <FileText className="h-4 w-4 text-blue-500" />;
      case 'approval_request':
        return <Send className="h-4 w-4 text-amber-500" />;
      case 'welcome':
        return <UserPlus className="h-4 w-4 text-green-500" />;
      case 'test':
        return <Mail className="h-4 w-4 text-purple-500" />;
      default:
        return <Mail className="h-4 w-4 text-gray-500" />;
    }
  };

  const getEmailTypeLabel = (type) => {
    switch (type) {
      case 'report_final':
        return 'Rapporto Finale';
      case 'approval_request':
        return 'Richiesta Approvazione';
      case 'welcome':
        return 'Benvenuto';
      case 'test':
        return 'Test';
      default:
        return type;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'success':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100"><CheckCircle className="h-3 w-3 mr-1" />Inviato</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100"><XCircle className="h-3 w-3 mr-1" />Fallito</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100"><Clock className="h-3 w-3 mr-1" />In Attesa</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6" data-testid="email-monitor-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1B6B93]">Monitoraggio Email</h1>
          <p className="text-gray-500 text-sm sm:text-base">Tracciamento invii e statistiche in tempo reale</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={fetchData} 
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
          <Button 
            variant="outline" 
            onClick={handleClearOldLogs}
            className="text-red-600 hover:text-red-700"
            data-testid="clear-logs-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Pulisci Vecchi
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card data-testid="stat-total-sent">
            <CardHeader className="pb-2">
              <CardDescription className="text-xs">Totale Inviati</CardDescription>
              <CardTitle className="text-2xl text-green-600">{stats.total_sent}</CardTitle>
            </CardHeader>
          </Card>
          <Card data-testid="stat-total-failed">
            <CardHeader className="pb-2">
              <CardDescription className="text-xs">Totale Falliti</CardDescription>
              <CardTitle className="text-2xl text-red-600">{stats.total_failed}</CardTitle>
            </CardHeader>
          </Card>
          <Card data-testid="stat-success-rate">
            <CardHeader className="pb-2">
              <CardDescription className="text-xs">Tasso di Successo</CardDescription>
              <CardTitle className="text-2xl text-[#1B6B93]">{stats.success_rate}%</CardTitle>
            </CardHeader>
          </Card>
          <Card data-testid="stat-today-sent">
            <CardHeader className="pb-2">
              <CardDescription className="text-xs">Oggi</CardDescription>
              <CardTitle className="text-2xl">
                <span className="text-green-600">{stats.today_sent}</span>
                {stats.today_failed > 0 && (
                  <span className="text-red-600 text-lg ml-2">(-{stats.today_failed})</span>
                )}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>
      )}

      {/* Additional Stats */}
      {stats && (
        <div className="grid grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="text-xs">Questa Settimana</CardDescription>
              <CardTitle className="text-xl text-[#1B6B93]">{stats.this_week_sent} inviate</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="text-xs">Questo Mese</CardDescription>
              <CardTitle className="text-xl text-[#1B6B93]">{stats.this_month_sent} inviate</CardTitle>
            </CardHeader>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Log Email</CardTitle>
          <div className="flex flex-wrap gap-4 mt-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Stato:</span>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]" data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="success">Inviati</SelectItem>
                  <SelectItem value="failed">Falliti</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Tipo:</span>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[180px]" data-testid="type-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="report_final">Rapporto Finale</SelectItem>
                  <SelectItem value="approval_request">Richiesta Approvazione</SelectItem>
                  <SelectItem value="welcome">Benvenuto</SelectItem>
                  <SelectItem value="test">Test</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[140px]">Data</TableHead>
                  <TableHead className="w-[140px]">Tipo</TableHead>
                  <TableHead>Destinatari</TableHead>
                  <TableHead>Rapporto</TableHead>
                  <TableHead className="w-[100px]">Stato</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                      {loading ? 'Caricamento...' : 'Nessun log trovato'}
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log.id} data-testid={`email-log-${log.id}`}>
                      <TableCell className="text-sm">{formatDate(log.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getEmailTypeIcon(log.email_type)}
                          <span className="text-sm">{getEmailTypeLabel(log.email_type)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-[200px] truncate text-sm" title={log.recipients?.join(', ')}>
                          {log.recipients?.join(', ') || '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          {log.report_number && (
                            <span className="text-sm font-medium text-[#1B6B93]">{log.report_number}</span>
                          )}
                          {log.site_name && (
                            <span className="text-xs text-gray-500 truncate max-w-[150px]">{log.site_name}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(log.status)}
                        {log.error_message && (
                          <div className="text-xs text-red-500 mt-1 max-w-[150px] truncate" title={log.error_message}>
                            {log.error_message}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteLog(log.id)}
                          className="text-gray-400 hover:text-red-500"
                          data-testid={`delete-log-${log.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
